/**
 * STEP OVERLAY-2D-TIMER · Shared Timer Control
 * Studio (SellerLiveControl) + Seller Console (SellerLiveConsole) 모두 재사용.
 * 전용 Console endpoint 없음 — 동일 /seller/broadcasts/{scheduleId}/timer 호출.
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

// STEP OVERLAY-REAL-UI-POLISH · 30초(0.5분) preset 추가 (지시 §6·§7).
// 짧은 시간대 (티저·다음 상품 공개 예고) UX. 내부값은 duration_seconds=30 그대로 REUSE.
const PRESETS = [30, 60, 180, 300, 600, 900];   // 30초·1·3·5·10·15분
const _presetLabel = (s) => (s < 60 ? `${s}초` : `${s/60}분`);

const _fmt = (sec) => {
  const s = Math.max(0, parseInt(sec || 0, 10));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// onLocalAction — 테스트 모드 전용: 서버 호출 없이 로컬 상태만 변경.
const OverlayTimerControl = ({ scheduleId, timer, disabled, compact, onLocalAction, 'data-testid': dti }) => {
  const [label, setLabel] = useState(() => (timer?.label || ''));
  const [minutes, setMinutes] = useState(10);
  const [busy, setBusy] = useState(false);

  const status = timer?.status || 'idle';
  const visible = timer?.visible !== false;
  const isDisabled = !!disabled || busy || !scheduleId;

  const send = async (action, extra = {}) => {
    if (isDisabled) return;
    if (onLocalAction) { onLocalAction(action, extra); return; }
    setBusy(true);
    try {
      await api.post(`/seller/broadcasts/${scheduleId}/timer`, { action, ...extra });
    } catch (e) { toast.error(e?.response?.data?.detail || `타이머 ${action} 실패`); }
    finally { setBusy(false); }
  };

  const start = () => {
    const dur = Math.max(1, Math.min(10800, Math.round((Number(minutes) || 0) * 60)));
    if (dur < 1) { toast.error('시간을 입력해주세요.'); return; }
    send('start', { duration_seconds: dur, label: label || '타이머' });
  };

  return (
    <div className={`rounded-xl border border-black/10 dark:border-white/10 ${compact ? 'p-2' : 'p-3'} space-y-2`}
         data-testid={dti || 'overlay-timer-control'}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-black tracking-widest uppercase text-gray-500">⏱ 타이머</span>
        <span className="text-[11px] font-bold" data-testid="overlay-timer-status">
          {status === 'running' && `진행중 · ${_fmt(timer?.remaining_seconds)}`}
          {status === 'paused'  && `일시정지 · ${_fmt(timer?.remaining_seconds)}`}
          {status === 'finished' && '완료 · 00:00'}
          {status === 'idle' && '대기'}
        </span>
        {!visible && <span className="text-[10px] text-gray-400">(hidden)</span>}
      </div>
      {/* STEP OVERLAY-REAL-UI-POLISH · 설명/예시 (지시 §5). 공지/Event 기능 구분 ❌. */}
      <p className="text-[10px] text-gray-500 leading-snug"
         data-testid="overlay-timer-helper">
        방송 화면에 남은 시간을 표시해요. · 예: 공구 마감 · 이벤트 종료 · 특가 종료 · 다음 상품 공개
      </p>

      <div className="flex items-center gap-1 flex-wrap">
        <input type="text" value={label} onChange={(e) => setLabel(e.target.value.slice(0, 24))}
               placeholder="예: 공구 마감 · 이벤트 종료 · 특가 종료"
               className="px-2 py-1 rounded border border-black/10 dark:border-white/10 bg-transparent text-[11px] font-bold w-36"
               data-testid="overlay-timer-label-input"/>
        <input type="number" min={0.5} max={180} step={0.5} value={minutes}
               onChange={(e) => setMinutes(e.target.value)}
               className="px-2 py-1 rounded border border-black/10 dark:border-white/10 bg-transparent text-[11px] font-bold w-14"
               data-testid="overlay-timer-minutes-input"/>
        <span className="text-[10px] text-gray-500">분</span>
        {PRESETS.map((s) => (
          <button key={s} onClick={() => setMinutes(s / 60)} disabled={isDisabled}
                  className="px-1.5 py-0.5 rounded text-[10px] font-bold border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10"
                  data-testid={`overlay-timer-preset-${s}`}>{_presetLabel(s)}</button>
        ))}
      </div>

      <div className="flex items-center gap-1 flex-wrap">
        <button onClick={start} disabled={isDisabled}
                className="px-3 py-1 rounded-full text-[11px] font-black bg-[#FF3B30] text-white disabled:opacity-40"
                data-testid="overlay-timer-start">▶ 시작</button>
        <button onClick={() => send('pause')} disabled={isDisabled || status !== 'running'}
                className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-black/10 dark:border-white/10 disabled:opacity-40"
                data-testid="overlay-timer-pause">⏸ 멈춤</button>
        <button onClick={() => send('resume')} disabled={isDisabled || status !== 'paused'}
                className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-black/10 dark:border-white/10 disabled:opacity-40"
                data-testid="overlay-timer-resume">▶ 재개</button>
        <button onClick={() => send('reset')} disabled={isDisabled || !timer}
                className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-black/10 dark:border-white/10 disabled:opacity-40"
                data-testid="overlay-timer-reset">↻ 리셋</button>
        <button onClick={() => send(visible ? 'hide' : 'show')} disabled={isDisabled || !timer}
                className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-black/10 dark:border-white/10 disabled:opacity-40"
                data-testid="overlay-timer-visibility">{visible ? '🙈 숨김' : '👁 표시'}</button>
        <button onClick={() => send('stop')} disabled={isDisabled || !timer}
                className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-[#FF3B30] text-[#FF3B30] disabled:opacity-40"
                data-testid="overlay-timer-stop">⏹ 중지</button>
      </div>
    </div>
  );
};

export default OverlayTimerControl;
